import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, ArrowRight } from 'lucide-react';

const PRIORITIES = { baja: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20', media: 'bg-amber-500/10 text-amber-500 border-amber-500/20', alta: 'bg-red-500/10 text-red-400 border-red-500/20' };
const STATUS_LABELS = { pendiente: 'Pendiente', en_progreso: 'En Progreso', hecho: 'Hecho' };
const STATUS_COLORS = { pendiente: 'border-amber-500/20 bg-amber-500/5', en_progreso: 'border-blue-500/20 bg-blue-500/5', hecho: 'border-emerald-500/20 bg-emerald-500/5' };

export default function Tickets() {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', priority: 'media', status: 'pendiente', date: new Date().toISOString().split('T')[0], start_time: '', end_time: '', tags: '', assigned_to: [] });

  const load = useCallback(async () => {
    try {
      const [t, u] = await Promise.all([API.get('/tickets'), API.get('/users').catch(() => ({ data: [] }))]);
      setTickets(t.data);
      setUsers(u.data);
    } catch { toast.error('Error cargando tickets'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ title: '', description: '', priority: 'media', status: 'pendiente', date: new Date().toISOString().split('T')[0], start_time: '', end_time: '', tags: '', assigned_to: [] });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title) { toast.error('El titulo es obligatorio'); return; }
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        start_time: form.start_time || undefined,
        end_time: form.end_time || undefined,
      };
      await API.post('/tickets', payload);
      toast.success('Ticket creado');
      setDialogOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleStatusChange = async (tid, newStatus) => {
    try {
      await API.put(`/tickets/${tid}`, { status: newStatus });
      toast.success('Estado actualizado');
      load();
    } catch { toast.error('Error actualizando'); }
  };

  const umap = Object.fromEntries(users.map(u => [u.id, u.name]));
  const toggleAssigned = (uid) => {
    setForm(f => ({
      ...f,
      assigned_to: f.assigned_to.includes(uid) ? f.assigned_to.filter(id => id !== uid) : [...f.assigned_to, uid]
    }));
  };

  const columns = ['pendiente', 'en_progreso', 'hecho'];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tickets-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-zinc-50">Tickets</h1>
          <p className="text-sm text-zinc-500">Gestion de tareas y asignaciones</p>
        </div>
        {hasPermission('tickets.create') && (
          <Button onClick={openCreate} className="bg-amber-500 hover:bg-amber-600 text-black font-medium" data-testid="create-ticket-button">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Ticket
          </Button>
        )}
      </div>

      {/* Kanban */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {columns.map(status => (
          <div key={status} className={`rounded-lg border p-4 kanban-col ${STATUS_COLORS[status]}`} data-testid={`kanban-column-${status}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-zinc-300">{STATUS_LABELS[status]}</h3>
              <Badge variant="outline" className="text-xs text-zinc-500 border-white/10">
                {tickets.filter(t => t.status === status).length}
              </Badge>
            </div>
            <div className="space-y-2">
              {tickets.filter(t => t.status === status).map(t => (
                <div key={t.id}
                  className="p-3 rounded-lg bg-zinc-900/60 border border-white/5 hover:border-white/10 cursor-pointer transition-colors group"
                  onClick={() => navigate(`/tickets/${t.id}`)}
                  data-testid={`ticket-card-${t.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="text-sm font-medium text-zinc-200 line-clamp-2">{t.title}</h4>
                    <Badge variant="outline" className={`text-[10px] shrink-0 ${PRIORITIES[t.priority]}`}>{t.priority}</Badge>
                  </div>
                  {t.description && <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{t.description}</p>}
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex flex-wrap gap-1">
                      {(t.tags || []).map(tag => (
                        <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-500">{tag}</span>
                      ))}
                    </div>
                    <div className="flex -space-x-1">
                      {(t.assigned_to || []).slice(0, 3).map(uid => (
                        <div key={uid} className="w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center text-[9px] text-amber-500 border border-zinc-900 font-medium"
                          title={umap[uid]}>
                          {(umap[uid] || '?').charAt(0)}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="text-[10px] text-zinc-600 mt-1.5">{t.date}{t.start_time ? ` ${t.start_time}-${t.end_time}` : ''}</div>
                </div>
              ))}
              {tickets.filter(t => t.status === status).length === 0 && (
                <p className="text-xs text-zinc-600 text-center py-6">Sin tickets</p>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 sm:max-w-lg" data-testid="ticket-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 font-heading">Nuevo Ticket</DialogTitle>
            <DialogDescription className="text-zinc-500">Crea una nueva tarea o asignacion</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div className="space-y-2">
              <Label className="text-zinc-300">Titulo</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                className="bg-zinc-950/50 border-white/10" data-testid="ticket-title-input" aria-label="Titulo" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Descripcion</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                className="bg-zinc-950/50 border-white/10 min-h-[80px]" aria-label="Descripcion" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-zinc-300">Prioridad</Label>
                <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                  <SelectTrigger className="bg-zinc-950/50 border-white/10" data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-white/10">
                    <SelectItem value="baja">Baja</SelectItem>
                    <SelectItem value="media">Media</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Fecha</Label>
                <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                  className="bg-zinc-950/50 border-white/10" data-testid="ticket-date-input" aria-label="Fecha" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-zinc-300">Hora inicio (opc.)</Label>
                <Input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))}
                  className="bg-zinc-950/50 border-white/10" aria-label="Hora inicio" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Hora fin (opc.)</Label>
                <Input type="time" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))}
                  className="bg-zinc-950/50 border-white/10" aria-label="Hora fin" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Etiquetas (separadas por coma)</Label>
              <Input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
                placeholder="limpieza, stock" className="bg-zinc-950/50 border-white/10" aria-label="Etiquetas" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Asignar a</Label>
              <div className="flex flex-wrap gap-2">
                {users.map(u => (
                  <button key={u.id} onClick={() => toggleAssigned(u.id)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${form.assigned_to.includes(u.id) ? 'bg-amber-500/20 text-amber-500 border border-amber-500/30' : 'bg-zinc-800 text-zinc-400 border border-white/5 hover:border-white/10'}`}
                    data-testid={`assign-user-${u.id}`}>
                    {u.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialogOpen(false)} className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200">Cancelar</Button>
            <Button onClick={handleSave} className="bg-amber-500 hover:bg-amber-600 text-black font-medium" data-testid="ticket-save-button">Crear Ticket</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
